import { Box } from "@mui/material";
import { ReactNode, createContext, useContext } from "react";

/**
 * A way for a form to render something underneath one of its fields without the
 * field's call site knowing about it.
 *
 * The review surfaces need a row of chips beneath every input, and their inputs
 * are built through shared controls used on about sixty call sites. Threading a
 * node through each of them would touch every one of those sites to deliver
 * content none of them has an opinion about. A provider keyed by field name
 * inverts that: the form says what belongs under a given field, and the control
 * asks for it by the name it already has.
 *
 * Deliberately generic. The control that renders this does not know it is showing
 * offered values, so nothing about the review surfaces leaks into a component
 * every form in the app uses.
 */
const FieldAccessoryContext = createContext<
  ((field: { name: string; label?: ReactNode }) => ReactNode) | null
>(null);

export function FieldAccessoryProvider({
  render,
  children,
}: {
  /**
   * What belongs under this field, or null for nothing.
   *
   * The control passes its own `label` along with the name, because it is the
   * only thing that knows it — the form has the field's path, but "1. Generator
   * ID Number" lives at the call site. Anything needing to name the field for a
   * screen reader wants that, not the path.
   *
   * Called during the field's own render, so keep it cheap and free of side
   * effects — on a manifest it runs sixty times per keystroke.
   */
  render: (field: { name: string; label?: ReactNode }) => ReactNode;
  children: ReactNode;
}) {
  return <FieldAccessoryContext.Provider value={render}>{children}</FieldAccessoryContext.Provider>;
}

/**
 * What the surrounding form wants rendered under this field, if anything.
 *
 * Null outside a provider, which is the common case — most forms in the app have
 * nothing to add and pay only a context read.
 */
export function useFieldAccessory(name: string | undefined, label?: ReactNode): ReactNode {
  const render = useContext(FieldAccessoryContext);
  if (render == null || name == null) return null;
  return render({ name, label });
}

/**
 * Where an accessory goes on a MUI `TextField`: the helper-text slot, above
 * whatever the field has to say about itself.
 *
 * The helper slot rather than a wrapper around the input, because these controls
 * are used with flex `sx` inside row `Stack`s — an extra element between the
 * stack and the field takes the flex rule with it and the row collapses. The
 * helper text is already inside the field's own box and already grows it
 * downward.
 *
 * A validation message stays last, so the final thing read is the one saying the
 * value is wrong rather than that another value is available.
 */
export function fieldAccessorySlot(
  accessory: ReactNode,
  message: ReactNode
): { helperText: ReactNode; formHelperText?: { component: "div" } } {
  if (accessory == null) return { helperText: message };
  return {
    helperText: (
      <>
        {/* Cancels the chip's own label padding so a chip's text starts where the
            message below it starts. Without it the row reads as a second, deeper
            indent against the only other line of text in the field. The right
            side matches, so a narrow column gives its chips the same inset on
            both sides instead of the helper text's wider one. */}
        <Box sx={{ mx: "-8px" }}>{accessory}</Box>
        {message}
      </>
    ),
    // Chips are buttons, and the default helper text is a `<p>`, which cannot
    // legally contain them.
    formHelperText: { component: "div" },
  };
}
